import { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { PLACEHOLDER_COLOR } from '@/components/@IntranetLibrary/DatePicker/styles';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import {
  buildStyleMasterPriceHtml,
  StyleMasterPriceSnapshot,
  type StyleMasterPriceTarget,
} from '@/components/Approval/@components/StyleMasterPrice';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getStyleMasterPriceDraftSourceUrl } from '@/constant/approval';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildStyleMasterPriceEditorContent,
  buildStyleMasterPriceLabel,
  buildStyleMasterPriceSubject,
  type StyleMasterPriceCandidate,
  type StyleMasterPriceDraftSource,
  type StyleMasterPriceItem,
  type StyleMasterPriceRow,
} from '@/utils/approval/styleMasterPrice';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean; editorApi: ReplaceContentApi | null };

/**
 * GERP STYLE MASTER 가격 변경(128) — 내 미상신 건을 여러 개 체크하면 건마다 가격 변경 행을 불러옴.
 * 고르거나 뺄 때마다 제목·본문을 덮어씀. 편집은 건을 바꿀 수 없고 저장 표만
 */
const StyleMasterPriceField = ({ isEditMode, editorApi }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const dialog = useDialog();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const saved = useWatch({ control, name: 'styleMasterPrice' });

  /** 고른 순서 그대로 */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowsById, setRowsById] = useState<
    Record<string, StyleMasterPriceRow[]>
  >({});
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  // 행 응답(비동기)이 최신 선택·행을 보도록 state 와 같이 둠
  const selectedRef = useRef<string[]>([]);
  const rowsRef = useRef<Record<string, StyleMasterPriceRow[]>>({});

  const { data: source, isLoading } = useSWR<StyleMasterPriceDraftSource>(
    isEditMode ? null : getStyleMasterPriceDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  const candidateById = useMemo(
    () =>
      new Map<string, StyleMasterPriceCandidate>(
        (source?.display.candidates ?? []).map((c) => [c.aprvId, c]),
      ),
    [source],
  );

  const styleNoOf = (id: string) => {
    const c = candidateById.get(id);
    return c ? buildStyleMasterPriceLabel(c) : id;
  };

  const toItems = (
    ids: string[],
    loaded: Record<string, StyleMasterPriceRow[]>,
  ): StyleMasterPriceItem[] =>
    ids.flatMap((id) =>
      loaded[id]
        ? [{ aprvId: id, styleNo: styleNoOf(id), rows: loaded[id] }]
        : [],
    );

  /** 행을 다 받았을 때만 저장 값 — 덜 받았으면 상신 시 안내 */
  const syncSaved = (
    ids: string[],
    loaded: Record<string, StyleMasterPriceRow[]>,
  ) => {
    const first = candidateById.get(ids[0] ?? '');
    const isReady = !!first && ids.every((id) => loaded[id]);
    setValue(
      'styleMasterPrice',
      isReady
        ? {
            systemId: first.sysId,
            approvalTypeCode: first.aprvTypcd,
            approvalId: ids.join(','),
            content: buildStyleMasterPriceHtml(toItems(ids, loaded), t),
          }
        : null,
      { shouldDirty: true },
    );
  };

  /** 레거시 change_title_content — 고르거나 뺄 때마다 제목·본문 덮어씀 */
  const overwriteSubjectAndBody = (ids: string[]) => {
    const styleNos = ids.map(styleNoOf);
    setValue('subject', buildStyleMasterPriceSubject(styleNos, t), {
      shouldDirty: true,
    });
    editorApi?.replace(buildStyleMasterPriceEditorContent(styleNos, t));
  };

  const update = (
    ids: string[],
    loaded: Record<string, StyleMasterPriceRow[]>,
  ) => {
    selectedRef.current = ids;
    rowsRef.current = loaded;
    setSelectedIds(ids);
    setRowsById(loaded);
  };

  const loadRows = async (id: string) => {
    setLoadingIds((prev) => [...prev, id]);
    try {
      const data = await getNestedDataFetcher<StyleMasterPriceDraftSource>(
        getStyleMasterPriceDraftSourceUrl([id]),
      );
      const rows = data.display.content?.rows;
      if (!rows?.length) throw new Error('rows missing');
      // 응답 전에 체크를 풀었으면 버림
      if (!selectedRef.current.includes(id)) return;
      const next = { ...rowsRef.current, [id]: rows };
      update(selectedRef.current, next);
      syncSaved(selectedRef.current, next);
    } catch {
      if (!selectedRef.current.includes(id)) return;
      // 같은 건을 다시 골라 재시도할 수 있게 선택에서 뺌
      const next = selectedRef.current.filter((k) => k !== id);
      update(next, rowsRef.current);
      overwriteSubjectAndBody(next);
      syncSaved(next, rowsRef.current);
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      setLoadingIds((prev) => prev.filter((k) => k !== id));
    }
  };

  const applySelection = (nextIds: string[]) => {
    const added = nextIds.filter((id) => !selectedRef.current.includes(id));
    const loaded = { ...rowsRef.current };
    selectedRef.current
      .filter((id) => !nextIds.includes(id))
      .forEach((id) => delete loaded[id]);
    update(nextIds, loaded);
    overwriteSubjectAndBody(nextIds);
    syncSaved(nextIds, loaded);
    added.forEach(loadRows);
  };

  const handleDelete = ({ itemId, styleNo }: StyleMasterPriceTarget) =>
    dialog.show({
      message: t('{{styleNo}}를 삭제하시겠습니까?', { styleNo }),
      confirmButtonText: t('삭제'),
      cancelButtonText: t('취소'),
      onConfirm: () =>
        applySelection(selectedRef.current.filter((id) => id !== itemId)),
    });

  if (isEditMode)
    return (
      <FieldBlock>
        {saved?.content && (
          <StyleMasterPriceSnapshot
            content={saved.content}
            actionTitle={t('삭제')}
          />
        )}
      </FieldBlock>
    );

  const options: SearchableSelectOption[] = candidates.map((c) => {
    const label = buildStyleMasterPriceLabel(c);
    return {
      value: c.aprvId,
      label,
      labelNode: (
        <OptionLabel>
          <Checkbox
            size="small"
            checked={selectedIds.includes(c.aprvId)}
            tabIndex={-1}
            sx={{ p: 0, pointerEvents: 'none' }}
          />
          {label}
        </OptionLabel>
      ),
    };
  });
  const placeholder = t('결재할 건을 선택해주세요');
  const items = toItems(selectedIds, rowsById);

  return (
    <FieldBlock>
      <ControlRow>
        <SearchableSelect
          multiple
          value={selectedIds}
          options={options}
          placeholder={isLoading ? t('불러오는 중...') : placeholder}
          noOptionsText={isLoading ? null : t('진행할 내역이 없습니다.')}
          // 레거시 콤보처럼 고른 건 대신 안내 문구 — 고른 건은 아래 표
          renderValue={() => <Placeholder>{placeholder}</Placeholder>}
          onChange={applySelection}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        {loadingIds.length > 0 && <Spinner theme="black" size="16px" />}
      </ControlRow>
      {items.length > 0 && (
        <StyleMasterPriceSnapshot
          content={buildStyleMasterPriceHtml(items, t)}
          actionTitle={t('삭제')}
          onDelete={handleDelete}
        />
      )}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const OptionLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const Placeholder = styled('span')({ color: PLACEHOLDER_COLOR });

export default StyleMasterPriceField;
